import { Pagination } from '@mui/material';
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FarmerGroupSummaryProperties } from '@/api/schemas';
import arrowBackSvg from '@/assets/svg/arrow-back.svg';
import bag from '@/assets/svg/Description/bag.svg';
import circle from '@/assets/svg/Description/circle.svg';
import clouds from '@/assets/svg/Description/clouds.svg';
import flag from '@/assets/svg/Description/flag.svg';
import ico_forest from '@/assets/svg/Description/ico_forest.svg';
import person from '@/assets/svg/person.svg';
import { AccordionSection } from '@/components/AccordionSection/AccordionSection';
import {
  getFactoriesQuery,
  getFarmersGroupQuery,
  getFarmersQuery,
  getForestsGroupQuery,
  type Layer,
} from '@/components/Dashboard/queries';
import { Group } from '@/components/Dashboard/types';
import { FarmerCard } from '@/components/FarmerCard/FarmerCard';
import { FarmerModal } from '@/components/FarmerModal/FarmerModal';
import { ImageSlider } from '@/components/ImageSlider/ImageSlider';
import { JoinInSlideIn } from '@/components/JoinInSlideIn/JoinInSlideIn';
import { News } from '@/components/News/News';
import { getAccordionData, projectHeadingText, useDataPerPage } from '@/components/Project/helpers';
import {
  BackgroundIntro,
  BackMapButton,
  Container,
  Description,
  DescriptionBlock,
  DescriptionBlockText,
  DescriptionBlockTitle,
  FarmersGrid,
  ImpactDescription,
  LeftDescription,
  LeftDescriptionBlock,
  RightDescription,
  RightDescriptionBlock,
  Section,
  SectionTitle,
  Tab,
  Tabs,
  TabsContainer,
  Title,
  TitleBox,
} from '@/components/Project/Project.styles';
import { orderTreesForList } from '@/components/Project/treeOrder';
import { RichText } from '@/components/RichText/RichText';
import { TreeCard } from '@/components/TreeCard/TreeCard';
import { useLabels } from '@/hooks/useLabels';

interface ProjectProps {
  group: Group | null;
  setGroup: Dispatch<SetStateAction<Group | null>>;
  farmersData: Layer | undefined;
  treesData: Layer | undefined;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('[Project ErrorBoundary] render failed:', error);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong while rendering this project.</div>;
    }
    return this.props.children;
  }
}

export const Project = forwardRef<HTMLDivElement, ProjectProps>(
  ({ group, setGroup, farmersData, treesData }, ref) => {
    const DATA_PER_PAGE = useDataPerPage();
    // Tile labels come from key_impact_data_live.labels_json via t(); the fallback is
    // the exact text production showed before PR #7 (founder decision
    // 2026-09-06: no dashboard wording lives in code).
    const { t } = useLabels();

    const { data: farmersGroupData } = getFarmersGroupQuery();
    const { data: forestsGroupData } = getForestsGroupQuery();
    const { data: factoriesData } = getFactoriesQuery();
    const { data: localFarmersData } = getFarmersQuery();

    const gridName = group?.type === 'farmers' ? 'Farmers' : 'Trees';

    const impactRef = useRef<HTMLElement>(null);
    const gridRef = useRef<HTMLElement>(null);
    const newsRef = useRef<HTMLElement>(null);
    const backgroundRef = useRef<HTMLElement>(null);

    const sectionsRefs = useMemo(
      () => ({
        impact: impactRef,
        grid: gridRef,
        news: newsRef,
        background: backgroundRef,
      }),
      [],
    );

    const getGroupDataByType = (type: string) => {
      switch (type) {
        case 'farmers':
          return farmersGroupData;
        case 'forests':
          return forestsGroupData;
        case 'factories':
          return factoriesData;
        default:
          return null;
      }
    };

    const [activeTab, setActiveTab] = useState('');
    const [page, setPage] = useState(1);
    const [selectedFarmer, setSelectedFarmer] = useState(null);
    // Engagement signal for the slide-in capture: true once the visitor
    // opened a farmer card or a news story on this project page.
    const [engaged, setEngaged] = useState(false);

    const filteredGroup = getGroupDataByType(group?.type ?? '')?.features?.find(
      (f) => f.properties.title === group?.group,
    );
    // The group-summary layers are not yet Zod-validated at the boundary (their
    // properties arrive as `any`), so this cast only gives the reads below a
    // declared shape - it does not validate anything. See schemas.ts.
    const summary = filteredGroup?.properties as FarmerGroupSummaryProperties | undefined;

    let projectImages = [];
    try {
      projectImages = filteredGroup?.properties?.gallery_images
        ? JSON.parse(filteredGroup.properties.gallery_images.replace(/'/g, '"'))
        : [];
    } catch (e) {
      console.error('[DEBUG] projectImages parse failed:', e);
    }

    useEffect(() => {
      setPage(1);
    }, [group]);

    const getFilteredData = useCallback(() => {
      if (group?.type === 'farmers') {
        const effectiveFarmersData = farmersData ?? localFarmersData;
        if (!effectiveFarmersData || !effectiveFarmersData.features) {
          return [];
        }
        console.log('[DEBUG] group:', group);
        console.log('[DEBUG] filteredGroup:', filteredGroup);
        console.log('[DEBUG] filteredGroup properties:', filteredGroup?.properties);
        console.log(
          '[DEBUG] farmers feature[0] properties:',
          effectiveFarmersData.features[0]?.properties,
        );
        console.log(
          '[DEBUG] farmers feature[0] farmer_group:',
          effectiveFarmersData.features[0]?.properties?.farmer_group,
        );
        // Filter to the SELECTED cohort. Previously this only filtered by photo,
        // so every cohort showed the same all-farmers list (appeared stuck on
        // Kericho). group.farmerGroup comes from the clicked card (derived from the
        // pins). When absent, fall back to the old all-farmers behaviour.
        const filteredFarmers = effectiveFarmersData.features.filter((f) => {
          const hasPhoto =
            typeof f.properties.profile_photo === 'string' &&
            f.properties.profile_photo.startsWith('http');
          const matchesCohort =
            !group?.farmerGroup || f.properties.farmer_group === group.farmerGroup;
          return hasPhoto && matchesCohort;
        });
        console.log('Project.getFilteredData debug', {
          filteredGroup,
          filteredGroupFarmerGroup: filteredGroup?.properties.farmer_group,
          firstFeatureFarmerGroup: effectiveFarmersData.features[0]?.properties.farmer_group,
          filteredFarmersLength: filteredFarmers.length,
        });
        const uniqueFarmers = Array.from(
          new Map(
            filteredFarmers.map((farmer) => {
              const key = `${farmer.properties.first_name}_${farmer.properties.last_name}`;
              return [key, farmer];
            }),
          ).values(),
        );
        return uniqueFarmers.sort((a, b) => {
          const aHasPhoto = a.properties.profile_photo?.startsWith('http') ? 0 : 1;
          const bHasPhoto = b.properties.profile_photo?.startsWith('http') ? 0 : 1;
          return aHasPhoto - bHasPhoto;
        });
      } else if (group?.type === 'forests') {
        const trees = treesData?.features?.filter((f) => f.properties.farmer_group) || [];
        // Founder 2026-09-21 (trees-3): ranked FIRST by photo-URL pattern (live
        // patterns lead, known-dead + empty sink), then NEWEST PLANTED first
        // (planting_date desc), then id descending. This one sorted array feeds
        // both the page slice and the Pagination count below, so the list and the
        // count share the same ordering. See treeOrder.ts / PROGRESS Q36.
        return orderTreesForList(trees);
      }
      return [];
    }, [group, farmersData, localFarmersData, treesData, filteredGroup]);

    const filteredData = getFilteredData();

    const currentPaginationData = useMemo(
      () => filteredData.slice((page - 1) * DATA_PER_PAGE, page * DATA_PER_PAGE),
      [filteredData, page, DATA_PER_PAGE],
    );

    const isGridNotToShow =
      group?.type === 'factories' ||
      (group?.type === 'forests' && group?.group !== 'Trees, Ethiopia') ||
      (group?.type !== 'farmers' && !currentPaginationData.length);

    const handlePageChange = useCallback(
      (_: React.ChangeEvent<unknown>, value: number) => setPage(value),
      [],
    );

    const farmersAwaitingGeoServer =
      group?.type === 'farmers' && (farmersData ?? localFarmersData) === undefined;

    const renderGrid = useCallback(() => {
      if (group?.type === 'farmers') {
        return (
          <FarmersGrid>
            {farmersAwaitingGeoServer ? (
              <p>Loading farmers...</p>
            ) : filteredData.length === 0 ? (
              <p>No farmers found</p>
            ) : (
              currentPaginationData.map((farmer) => (
                <FarmerCard
                  key={farmer.id}
                  data={farmer.properties}
                  type="grid"
                  onClick={() => {
                    setEngaged(true);
                    setSelectedFarmer(farmer.properties);
                  }}
                />
              ))
            )}
          </FarmersGrid>
        );
      }
      if (group?.type === 'forests') {
        return (
          <FarmersGrid>
            {currentPaginationData.map((tree) => (
              <TreeCard key={tree.id} {...tree.properties} />
            ))}
          </FarmersGrid>
        );
      }
      return null;
    }, [group, currentPaginationData, farmersAwaitingGeoServer, filteredData.length]);

    const scrollToSection = (sectionName: keyof typeof sectionsRefs) => {
      const sectionTop = sectionsRefs[sectionName]?.current?.offsetTop ?? 0;

      window.scrollTo({
        top: sectionTop - 70,
        behavior: 'smooth',
      });

      setActiveTab(sectionName);
    };

    const handleScroll = useCallback(() => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const contentHeight = document.documentElement.scrollHeight;

      let newActiveTab: string | null = null;

      const visibleSections = Object.entries(sectionsRefs).filter(([key]) => {
        if (key === 'grid' && !sectionsRefs.grid.current) return false;
        if (key === 'background' && !sectionsRefs.background.current) return false;
        return true;
      });

      visibleSections.forEach(([key, ref], index) => {
        const sectionTop = ref.current?.offsetTop ?? 0;
        const sectionBottom = sectionTop + (ref.current?.offsetHeight ?? 0);

        if (scrollY + 70 >= sectionTop && scrollY < sectionBottom) {
          newActiveTab = key;
        }

        const isLastVisibleTab = index === visibleSections.length - 1;
        if (isLastVisibleTab && scrollY + windowHeight >= contentHeight - 10) {
          newActiveTab = key;
        }
      });

      if (newActiveTab && newActiveTab !== activeTab) {
        setActiveTab(newActiveTab);
      }
    }, [activeTab, sectionsRefs]);

    useEffect(() => {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, handleScroll]);

    // A value of "na", null or undefined hides the whole tile, and so does an
    // empty label: a figure with no words under it is a claim without context
    // (CLAUDE.md §10). Nothing is ever shown as a blank number.
    const renderDescriptionBlock = (
      icon: string,
      value: string | null | undefined,
      text: string,
    ) => (
      <>
        {value && value !== 'na' && text !== '' && (
          <DescriptionBlock>
            <LeftDescriptionBlock src={icon} type={group?.type} />
            <RightDescriptionBlock>
              <DescriptionBlockTitle>{value}</DescriptionBlockTitle>
              <DescriptionBlockText>{text}</DescriptionBlockText>
            </RightDescriptionBlock>
          </DescriptionBlock>
        )}
      </>
    );

    return (
      <ErrorBoundary>
        <Container ref={ref}>
          <TitleBox>
            <Title>
              Project: <b>{projectHeadingText(t, group?.group)}</b>
            </Title>
          </TitleBox>

          <Description>
            <LeftDescription>
              <BackMapButton onClick={() => setGroup(null)}>
                <img src={arrowBackSvg} />
                Back to map
              </BackMapButton>
            </LeftDescription>

            <RightDescription>
              {renderDescriptionBlock(person, summary?.farmers, t('tile.farmers.label', 'Farmers'))}
              {/* farmers_supplying: filled by founder-run migration 08 (Mizan "106",
                  Limu "827", others "na"). The tile shows only when the value is a
                  real string other than "na" AND the label row
                  tile.farmers_supplying.label exists - there is no pre-PR text to
                  fall back to, so no label means no tile. */}
              {renderDescriptionBlock(
                person,
                summary?.farmers_supplying,
                t('tile.farmers_supplying.label', ''),
              )}
              {renderDescriptionBlock(
                ico_forest,
                filteredGroup?.properties.ha_forest_protected,
                t('tile.ha_forest_protected.label', 'Forest protected'),
              )}
              {renderDescriptionBlock(
                bag,
                filteredGroup?.properties.jobs,
                t('tile.jobs.label', 'Jobs'),
              )}
              {renderDescriptionBlock(
                ico_forest,
                filteredGroup?.properties.trees_planted,
                t('tile.trees_planted.label', 'Trees planted'),
              )}
              {renderDescriptionBlock(
                bag,
                filteredGroup?.properties.kilos_processed,
                t('tile.kilos_processed.label', 'Processed'),
              )}
              {renderDescriptionBlock(
                clouds,
                filteredGroup?.properties.total_co2,
                t('tile.total_co2.label', 'CO2 Sequestered'),
              )}
              {renderDescriptionBlock(
                clouds,
                filteredGroup?.properties.ton_co2_sequestered,
                t('tile.ton_co2_sequestered.label', 'Mton CO2 Sequestered'),
              )}
              {renderDescriptionBlock(
                clouds,
                filteredGroup?.properties.kg_biocompost_used,
                t('tile.kg_biocompost_used.label', 'Biocompost made'),
              )}
              {renderDescriptionBlock(
                flag,
                filteredGroup?.properties.kg_roasted_last_year,
                t('tile.kg_roasted_last_year.label', 'Processed last year'),
              )}
              {renderDescriptionBlock(
                circle,
                filteredGroup?.properties.cups_drank,
                t('tile.cups_drank.label', 'Cups drank'),
              )}
            </RightDescription>
          </Description>

          {/* New cohorts (Bench Maji, Uganda) aren't in farmer_group_summary yet, so
            they have no gallery_images. Render the slider only when there are real
            images - otherwise it shows a broken "Main" placeholder and a tall empty
            gap. Fail cleanly instead. */}
          {projectImages.length > 0 && <ImageSlider projectImages={projectImages} />}

          <TabsContainer>
            <Tabs>
              <Tab
                active={activeTab === 'impact'}
                isFactory={isGridNotToShow}
                onClick={() => scrollToSection('impact' as keyof typeof sectionsRefs)}
              >
                Impact
              </Tab>
              {!isGridNotToShow && (
                <Tab
                  active={activeTab === 'grid'}
                  isFactory={isGridNotToShow}
                  onClick={() => scrollToSection('grid' as keyof typeof sectionsRefs)}
                >
                  {gridName}
                </Tab>
              )}
              <Tab
                active={activeTab === 'news'}
                isFactory={isGridNotToShow}
                onClick={() => scrollToSection('news' as keyof typeof sectionsRefs)}
              >
                News
              </Tab>
              <Tab
                active={activeTab === 'background'}
                isFactory={isGridNotToShow}
                onClick={() => scrollToSection('background' as keyof typeof sectionsRefs)}
              >
                Background
              </Tab>
            </Tabs>

            <Section ref={sectionsRefs.impact}>
              <SectionTitle>Impact</SectionTitle>
              <ImpactDescription>
                <div>
                  <RichText text={summary?.intro_text} />
                </div>
                <div>
                  <img src={filteredGroup?.properties.intro_image} />
                </div>
              </ImpactDescription>
            </Section>

            {!isGridNotToShow && (
              <Section ref={sectionsRefs.grid}>
                <SectionTitle>{gridName}</SectionTitle>
                {renderGrid()}
                <Pagination
                  count={Math.ceil(filteredData.length / DATA_PER_PAGE)}
                  page={page}
                  shape="rounded"
                  onChange={handlePageChange}
                  color="primary"
                  sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
                />
              </Section>
            )}

            <Section ref={sectionsRefs.news}>
              <SectionTitle>Latest news</SectionTitle>
              <News
                blogFilter={filteredGroup?.properties.blog_filter}
                project={group?.group}
                onStoryClick={() => setEngaged(true)}
              />
            </Section>

            <Section ref={sectionsRefs.background}>
              <SectionTitle>Background</SectionTitle>
              <BackgroundIntro>
                <RichText text={summary?.background_intro} />
              </BackgroundIntro>
              <AccordionSection
                items={getAccordionData(filteredGroup?.properties)}
                to={filteredGroup?.properties.lirp_report}
              />
            </Section>
          </TabsContainer>
          {selectedFarmer && (
            <FarmerModal data={selectedFarmer} onClose={() => setSelectedFarmer(null)} />
          )}
          {/* Project page only, never the map/home view (founder spec 2026-09-03). */}
          <JoinInSlideIn engaged={engaged} project={group?.group} />
        </Container>
      </ErrorBoundary>
    );
  },
);

Project.displayName = 'Project';
